import React from 'react';
import { useTranslation } from 'react-i18next';
import Btn from '../Btn/Btn';
import SpinnerBox from '../Spinner/SpinnerBox';
import { QuestionnaireSummary, TaskSummary } from './types';
import { QuestionnaireKind, isNachsorgeKind, isTherapyMaterialKind } from '../../types/QuestionnaireKind';
import { isExpiredUnanswered } from './utils';
import { MAX_COMBINED_PDF_RESPONSES } from '../../helpers/pdf/combinedResponsesPdf';
import './TaskList.css';

export type TaskListSelection = {
    selectedTaskIds: Set<string>;
    // Only tasks with a saved response can be exported.
    isSelectable: (task: TaskSummary) => boolean;
    onChange: (selectedTaskIds: Set<string>) => void;
    onDownloadPdf: () => void;
    progress: { done: number; total: number } | null;
    feedback: { type: 'error' | 'warning'; message: string } | null;
};

type Props = {
    tasks: TaskSummary[];
    questionnaires: QuestionnaireSummary[];
    isLoading: boolean;
    error: string | null;
    searchTerm: string;
    onSearchChange: (term: string) => void;
    onTaskClick: (task: TaskSummary) => void;
    onDelete?: (task: TaskSummary) => void;
    accessToken: string | undefined;
    resourceKind: QuestionnaireKind;
    selection?: TaskListSelection;
};

const TaskList = ({
    tasks,
    questionnaires,
    isLoading,
    error,
    searchTerm,
    onSearchChange,
    onTaskClick,
    onDelete,
    accessToken,
    resourceKind,
    selection,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const isTherapyMaterial = isTherapyMaterialKind(resourceKind);
    const isNachsorge = isNachsorgeKind(resourceKind);
    const keepsLatestResponse = isTherapyMaterial || isNachsorge;
    const [now, setNow] = React.useState(() => Date.now());

    React.useEffect(() => {
        const nextDeadline = tasks
            .filter((task) => task.patientId && task.endsOn && !task.hasResponse)
            .map((task) => new Date(task.endsOn as string).getTime())
            .filter((deadline) => Number.isFinite(deadline) && deadline > now)
            .sort((a, b) => a - b)[0];
        if (nextDeadline === undefined) {
            return;
        }

        const timeout = window.setTimeout(
            () => setNow(Date.now()),
            Math.min(Math.max(nextDeadline - now + 10, 10), 2147483647),
        );
        return () => window.clearTimeout(timeout);
    }, [tasks, now]);
    const questionnaireDisplayNamesById = React.useMemo(
        () =>
            new Map(
                questionnaires.map((questionnaire) => [
                    questionnaire.id,
                    questionnaire.displayName ||
                        questionnaire.name?.trim() ||
                        questionnaire.title?.trim() ||
                        questionnaire.id,
                ]),
            ),
        [questionnaires],
    );

    const getQuestionnaireDisplayName = (task: TaskSummary): string => {
        const questionnaireNameFromList = task.questionnaireId
            ? questionnaireDisplayNamesById.get(task.questionnaireId)
            : undefined;
        return questionnaireNameFromList || task.questionnaireName || task.questionnaireId || t('Unknown');
    };

    const filteredTasks = tasks.filter((task) => {
        if (!searchTerm.trim()) {
            return true;
        }
        const search = searchTerm.toLowerCase();
        const questionnaireDisplayName = getQuestionnaireDisplayName(task).toLowerCase();
        return (
            task.patientName?.toLowerCase().includes(search) ||
            task.patientId?.toLowerCase().includes(search) ||
            questionnaireDisplayName.includes(search) ||
            task.questionnaireName?.toLowerCase().includes(search) ||
            task.questionnaireId?.toLowerCase().includes(search)
        );
    });

    const selectableTasks = selection ? filteredTasks.filter(selection.isSelectable) : [];
    const selectedCount = selection?.selectedTaskIds.size ?? 0;
    const isExportingPdf = Boolean(selection?.progress);
    const isSelectionFull = selectedCount >= MAX_COMBINED_PDF_RESPONSES;
    const areAllSelectableSelected =
        selectableTasks.length > 0 && selectableTasks.every((task) => selection?.selectedTaskIds.has(task.id));

    const toggleTask = (task: TaskSummary): void => {
        if (!selection) {
            return;
        }
        const next = new Set(selection.selectedTaskIds);
        if (next.has(task.id)) {
            next.delete(task.id);
        } else if (next.size < MAX_COMBINED_PDF_RESPONSES) {
            next.add(task.id);
        }
        selection.onChange(next);
    };

    const toggleAllSelectable = (): void => {
        if (!selection) {
            return;
        }
        const next = new Set(selection.selectedTaskIds);
        if (areAllSelectableSelected) {
            selectableTasks.forEach((task) => next.delete(task.id));
        } else {
            selectableTasks
                .filter((task) => !next.has(task.id))
                .slice(0, MAX_COMBINED_PDF_RESPONSES - next.size)
                .forEach((task) => next.add(task.id));
        }
        selection.onChange(next);
    };

    if (!accessToken) {
        return (
            <div className="frontpage__info-message">
                {isNachsorge
                    ? t('Log in to load aftercare tasks.')
                    : isTherapyMaterial
                    ? t('Log in to load assigned therapy material.')
                    : t('Log in to load tasks.')}
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="frontpage__loading">
                <SpinnerBox />
                <p>
                    {isNachsorge
                        ? t('Loading aftercare tasks...')
                        : isTherapyMaterial
                        ? t('Loading assigned therapy material...')
                        : t('Loading tasks...')}
                </p>
            </div>
        );
    }

    return (
        <>
            {tasks.length > 0 && (
                <input
                    type="text"
                    className="frontpage__search-input"
                    placeholder={t('Search patient...')}
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
                />
            )}
            {error && (
                <div className="frontpage__error">
                    {isNachsorge
                        ? t('Failed to load aftercare tasks')
                        : isTherapyMaterial
                        ? t('Failed to load assigned therapy material')
                        : t('Failed to load tasks')}
                    : {error}
                </div>
            )}
            {selection && (selectableTasks.length > 0 || selectedCount > 0) && (
                <div className="frontpage__task-selection">
                    <div className="frontpage__task-selection-bar">
                        <label className="frontpage__task-select-all">
                            <input
                                type="checkbox"
                                checked={areAllSelectableSelected}
                                onChange={toggleAllSelectable}
                                disabled={isExportingPdf || selectableTasks.length === 0}
                            />
                            {t('Select all with responses')}
                        </label>
                        <span className="frontpage__task-selection-count">
                            {t('{{count}} selected', { count: selectedCount })}
                        </span>
                        <div className="frontpage__task-selection-actions">
                            {selectedCount > 0 && (
                                <Btn
                                    title={t('Clear selection')}
                                    variant="secondary"
                                    onClick={() => selection.onChange(new Set())}
                                    disabled={isExportingPdf}
                                />
                            )}
                            <Btn
                                title={
                                    selection.progress
                                        ? t('Creating PDF... ({{done}} / {{total}})', selection.progress)
                                        : t('Download selected as PDF ({{count}})', { count: selectedCount })
                                }
                                variant="primary"
                                onClick={selection.onDownloadPdf}
                                disabled={selectedCount === 0 || isExportingPdf}
                            />
                        </div>
                    </div>
                    {isSelectionFull && (
                        <p className="frontpage__task-selection-hint">
                            {t('You can export up to {{max}} responses at once.', {
                                max: MAX_COMBINED_PDF_RESPONSES,
                            })}
                        </p>
                    )}
                    {selection.feedback && (
                        <p
                            className={`frontpage__task-selection-feedback frontpage__task-selection-feedback--${selection.feedback.type}`}
                            role={selection.feedback.type === 'error' ? 'alert' : 'status'}
                        >
                            {selection.feedback.message}
                        </p>
                    )}
                </div>
            )}
            {filteredTasks.length > 0 ? (
                <ul className="frontpage__list">
                    {filteredTasks.map((task) => {
                        const questionnaireDisplayName = getQuestionnaireDisplayName(task);
                        const expiredUnanswered = isExpiredUnanswered(task, now);
                        const isSelected = Boolean(selection?.selectedTaskIds.has(task.id));
                        return (
                            <li
                                key={task.id}
                                className={`frontpage__task-item ${
                                    expiredUnanswered ? 'frontpage__task-item--expired' : ''
                                }`}
                            >
                                {selection && (
                                    <input
                                        type="checkbox"
                                        className="frontpage__task-checkbox"
                                        aria-label={t('Select for PDF export')}
                                        checked={isSelected}
                                        onChange={() => toggleTask(task)}
                                        disabled={
                                            isExportingPdf ||
                                            !selection.isSelectable(task) ||
                                            (!isSelected && isSelectionFull)
                                        }
                                    />
                                )}
                                <div
                                    className="frontpage__task-content frontpage__task-content--clickable"
                                    onClick={() => onTaskClick(task)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onTaskClick(task);
                                        }
                                    }}
                                >
                                    <span className="frontpage__list-title">
                                        {t('Patient')}: {task.patientName || task.patientId || t('Unknown')}
                                    </span>
                                    <span className="frontpage__list-meta">
                                        {isNachsorge
                                            ? t('Aftercare material')
                                            : isTherapyMaterial
                                            ? t('Therapy Material')
                                            : t('Questionnaire')}
                                        : {questionnaireDisplayName}
                                    </span>
                                    <span className="frontpage__list-meta">
                                        {isNachsorge
                                            ? t('Aftercare material ID')
                                            : isTherapyMaterial
                                            ? t('Therapy Material ID')
                                            : t('Questionnaire ID')}
                                        : {task.questionnaireId || t('Unknown')}
                                    </span>
                                    {isNachsorge && task.week !== undefined && (
                                        <span className="frontpage__list-meta">
                                            {t('Aftercare week {{week}}', { week: task.week })}
                                        </span>
                                    )}
                                    {task.status && (
                                        <span className="frontpage__list-meta">
                                            {t('Status')}: {task.status}
                                        </span>
                                    )}
                                    {expiredUnanswered && (
                                        <span className="frontpage__task-expired-badge">
                                            {t('Expired – unanswered')}
                                        </span>
                                    )}
                                    {task.questionnaireLifecycle === 'replaced' && (
                                        <span
                                            className="frontpage__task-lifecycle-badge"
                                            title={t(
                                                'It was edited after patients had answered it. This assignment keeps the previous version.',
                                            )}
                                        >
                                            {t('Newer version exists')}
                                        </span>
                                    )}
                                    {task.questionnaireLifecycle === 'archived' && (
                                        <span
                                            className="frontpage__task-lifecycle-badge"
                                            title={t(
                                                'It was deleted while assigned. This assignment and its responses remain available.',
                                            )}
                                        >
                                            {t('Archived')}
                                        </span>
                                    )}
                                    {task.startsOn && (
                                        <span className="frontpage__list-meta">
                                            {t('Available from')}: {new Date(task.startsOn).toLocaleString()}
                                        </span>
                                    )}
                                    {task.endsOn && (
                                        <span className="frontpage__list-meta">
                                            {t('Available until')}: {new Date(task.endsOn).toLocaleString()}
                                        </span>
                                    )}
                                    {task.updatedAt && (
                                        <span className="frontpage__list-meta">
                                            {t('Updated at')}: {new Date(task.updatedAt).toLocaleString()}
                                        </span>
                                    )}
                                    {(task.status === 'completed' || keepsLatestResponse) && (
                                        <span className="frontpage__list-meta frontpage__list-meta--hint">
                                            {keepsLatestResponse
                                                ? task.hasResponse
                                                    ? t('Click to view the latest response')
                                                    : t('No response saved yet')
                                                : t('Click to view response')}
                                        </span>
                                    )}
                                </div>
                                {onDelete && (
                                    <Btn
                                        title={t('Delete')}
                                        variant="secondary"
                                        onClick={() => {
                                            onDelete(task);
                                        }}
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>
            ) : tasks.length > 0 ? (
                <div className="frontpage__empty">
                    {isNachsorge
                        ? t('No aftercare tasks found')
                        : isTherapyMaterial
                        ? t('No assigned therapy material found')
                        : t('No tasks found')}
                </div>
            ) : (
                <div className="frontpage__empty">
                    {isNachsorge
                        ? t('No aftercare tasks available yet')
                        : isTherapyMaterial
                        ? t('No therapy material assigned yet')
                        : t('No tasks available yet')}
                </div>
            )}
        </>
    );
};

export default TaskList;
